import React, { useState } from 'react';
import { Container, Card, Button, Table, Badge, Modal, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BuildingForm from '../components/BuildingForm';
import { useBuildings, useCreateBuilding, useUpdateBuilding, useDeleteBuilding } from '../hooks/useBuildings';
import { useCommunity } from '../hooks/useCommunities';
import { useToast } from '../components/ToastNotification';
import { createBuildingAsset, updateBuildingAsset, deleteBuildingAsset, getBuildingAssets } from '../api/buildingAssets';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';

/**
 * BuildingsSetupPage - Manage buildings within a community.
 *
 * Uses useParams to get communityId from URL.
 * Backend BuildingCreate schema:
 *   { building_id, building_type, floor_area_m2, energy_class, metadata }
 *
 * Data upload is handled on a dedicated Data page, not here.
 */
const BuildingsSetupPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { id: communityId } = useParams();
  const { canEdit } = useAuth();

  // Fetch community info
  const { data: community } = useCommunity(communityId);

  // Fetch buildings
  const { data: buildings = [], isLoading, isError, error } = useBuildings(communityId);

  // Mutations
  const createMutation = useCreateBuilding(communityId);
  const updateMutation = useUpdateBuilding(communityId);
  const deleteMutation = useDeleteBuilding(communityId);

  const [showModal, setShowModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);

  // -------------------------------------------------------------------
  // No communityId guard
  // -------------------------------------------------------------------
  if (!communityId) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">
          <Alert.Heading>{t('buildings_setup.no_community_title')}</Alert.Heading>
          <p>{t('buildings_setup.no_community_text')}</p>
          <Button variant="primary" onClick={() => navigate('/communities/new')}>
            {t('buildings_setup.create_community_btn')}
          </Button>
        </Alert>
      </Container>
    );
  }

  // -------------------------------------------------------------------
  // Building CRUD handlers
  // -------------------------------------------------------------------
  const handleAddBuilding = () => {
    setEditingBuilding(null);
    setShowModal(true);
  };

  const handleEditBuilding = (building) => {
    setEditingBuilding(building);
    setShowModal(true);
  };

  const handleDeleteBuilding = async (buildingId) => {
    if (!window.confirm(t('buildings_setup.delete_confirm'))) return;
    try {
      await deleteMutation.mutateAsync(buildingId);
      addToast(t('buildings_setup.delete_success'), 'success');
    } catch (err) {
      addToast(t('buildings_setup.delete_failure', { error: err.message }), 'danger');
    }
  };

  const handleSubmitBuilding = async (formData) => {
    // Map form data to BuildingCreate schema:
    // { building_id, building_type, floor_area_m2, energy_class, metadata }
    const payload = {
      building_id: formData.building_id,
      building_type: formData.building_type || 'residential',
      floor_area_m2: formData.floor_area ? parseFloat(formData.floor_area) : null,
      energy_class: formData.energy_class || null,
      metadata: {
        num_units: formData.num_units ? parseInt(formData.num_units) : null,
        num_floors: formData.num_floors ? parseInt(formData.num_floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        // Track building asset flags for UI convenience (kept for display in table)
        has_rooftop_pv: !!formData.has_rooftop_pv,
        has_battery: !!formData.has_battery,
        has_ev_charger: !!formData.has_ev_charger,
        has_heat_pump: !!formData.has_heat_pump,
        has_hvac: !!formData.has_hvac,
      },
    };

    // Asset types that can be attached to a building, mapped to their form keys
    const assetTypeKeys = [
      { assetType: 'rooftop_pv', hasKey: 'has_rooftop_pv', specsKey: 'rooftop_pv' },
      { assetType: 'battery', hasKey: 'has_battery', specsKey: 'battery' },
      { assetType: 'heat_pump', hasKey: 'has_heat_pump', specsKey: 'heat_pump' },
      { assetType: 'hvac', hasKey: 'has_hvac', specsKey: 'hvac' },
      { assetType: 'ev_charger', hasKey: 'has_ev_charger', specsKey: 'ev_charger' },
    ];

    try {
      let buildingId;

      if (editingBuilding) {
        await updateMutation.mutateAsync({
          buildingId: editingBuilding.id,
          data: payload,
        });
        buildingId = editingBuilding.id;
      } else {
        // createMutation returns the created building with its real DB UUID
        const createdBuilding = await createMutation.mutateAsync(payload);
        buildingId = createdBuilding.id;
      }

      // Sync building assets: create/update/delete based on form state.
      // We fetch the current building assets first to know which already exist.
      let existingAssets = [];
      try {
        existingAssets = await getBuildingAssets(communityId, buildingId);
      } catch (_err) {
        // If fetching fails (e.g. no assets yet), start with empty list
        existingAssets = [];
      }

      for (const { assetType, hasKey, specsKey } of assetTypeKeys) {
        const shouldHave = !!formData[hasKey];
        const existing = existingAssets.find(a => a.asset_type === assetType);
        const specs = formData[specsKey] || {};

        if (shouldHave && !existing) {
          // Create the building asset with its specs
          await createBuildingAsset(communityId, buildingId, { asset_type: assetType, specs });
        } else if (shouldHave && existing) {
          // Update specs if asset already exists
          await updateBuildingAsset(communityId, buildingId, existing.id, { specs });
        } else if (!shouldHave && existing) {
          // Remove the asset if the checkbox was unchecked
          await deleteBuildingAsset(communityId, buildingId, existing.id);
        }
      }

      addToast(
        editingBuilding ? t('buildings_setup.update_success') : t('buildings_setup.create_success'),
        'success'
      );
      setShowModal(false);
    } catch (err) {
      addToast(
        editingBuilding ? t('buildings_setup.update_failure', { error: err.message }) : t('buildings_setup.create_failure', { error: err.message }),
        'danger'
      );
    }
  };

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------
  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Helper to get building asset flags from metadata
  const getBuildingMeta = (building) => building.metadata || {};

  // -------------------------------------------------------------------
  // Loading / Error states
  // -------------------------------------------------------------------
  if (isLoading) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" role="status" style={{ color: theme.colors.primary.main }}>
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
        <p className="mt-3 text-muted">{t('buildings_setup.loading_buildings')}</p>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>{t('buildings_setup.load_failed')}</Alert.Heading>
          <p>{error?.message || t('buildings_setup.default_error')}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-2">
          {t('buildings_setup.header')}
        </h2>
        <p className="text-muted">
          {community?.name ? t('buildings_setup.subtitle_named', { name: community.name }) : t('buildings_setup.subtitle_default')}
        </p>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm" style={{ borderTop: `4px solid ${theme.colors.primary.main}` }}>
            <Card.Body>
              <h3 style={{ color: theme.colors.primary.main }}>{buildings.length}</h3>
              <div className="text-muted">{t('buildings_setup.total_buildings')}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm" style={{ borderTop: `4px solid ${theme.colors.energy?.solar || '#FFB800'}` }}>
            <Card.Body>
              <h3 style={{ color: theme.colors.energy?.solar || '#FFB800' }}>
                {buildings.filter(b => (b.building_assets || []).some(a => a.asset_type === 'rooftop_pv')).length}
              </h3>
              <div className="text-muted">{t('buildings_setup.with_solar_pv')}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center h-100 shadow-sm" style={{ borderTop: `4px solid ${theme.colors.energy?.battery || '#9B59B6'}` }}>
            <Card.Body>
              <h3 style={{ color: theme.colors.energy?.battery || '#9B59B6' }}>
                {buildings.filter(b => (b.building_assets || []).some(a => a.asset_type === 'battery')).length}
              </h3>
              <div className="text-muted">{t('buildings_setup.with_battery')}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Buildings Table */}
      <Card className="mb-4 shadow-sm">
        <Card.Header style={{ backgroundColor: theme.colors.neutral[50] }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{t('buildings_setup.buildings_list')}</h5>
            {canEdit && (
              <Button
                variant="primary"
                onClick={handleAddBuilding}
                style={{ backgroundColor: theme.colors.primary.main }}
                disabled={isMutating}
              >
                <span className="me-2">+</span>
                {t('buildings_setup.add_building')}
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {buildings.length > 0 ? (
            <Table hover responsive className="mb-0">
              <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                <tr>
                  <th>{t('buildings_setup.col_building_id')}</th>
                  <th>{t('buildings_setup.col_type')}</th>
                  <th>{t('buildings_setup.col_floor_area')}</th>
                  <th>{t('buildings_setup.col_energy_class')}</th>
                  <th>{t('buildings_setup.col_equipment')}</th>
                  <th className="text-end">{t('buildings_setup.col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {buildings.map(building => {
                  const bldgMeta = getBuildingMeta(building);
                  return (
                    <tr key={building.id}>
                      <td className="fw-semibold">{building.building_id}</td>
                      <td className="text-capitalize">{building.building_type}</td>
                      <td>{building.floor_area_m2 ? `${building.floor_area_m2} m2` : t('common.n_a')}</td>
                      <td>
                        {building.energy_class ? (
                          <Badge bg="secondary">{building.energy_class}</Badge>
                        ) : t('common.n_a')}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {bldgMeta.has_rooftop_pv && <Badge bg="warning">{t('buildings_setup.badge_pv')}</Badge>}
                          {bldgMeta.has_battery && <Badge bg="info">{t('buildings_setup.badge_battery')}</Badge>}
                          {bldgMeta.has_ev_charger && <Badge bg="success">{t('buildings_setup.badge_ev')}</Badge>}
                          {bldgMeta.has_heat_pump && <Badge bg="danger">{t('buildings_setup.badge_hp')}</Badge>}
                          {bldgMeta.has_hvac && <Badge bg="primary">{t('buildings_setup.badge_hvac')}</Badge>}
                        </div>
                      </td>
                      <td className="text-end">
                        {canEdit && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditBuilding(building)}
                          disabled={isMutating}
                        >
                          {t('buildings_setup.btn_edit')}
                        </Button>
                        )}
                        {canEdit && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteBuilding(building.id)}
                          disabled={isMutating}
                        >
                          {t('buildings_setup.btn_delete')}
                        </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info" className="m-4 text-center">
              <h4 className="mt-3">{t('buildings_setup.no_buildings_title')}</h4>
              <p className="mb-3">
                {t('buildings_setup.no_buildings_text')}
              </p>
              {canEdit && (
                <Button variant="outline-primary" onClick={handleAddBuilding} disabled={isMutating}>
                  <span className="me-2">+</span>
                  {t('buildings_setup.add_first_building')}
                </Button>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Building Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingBuilding ? t('buildings_setup.modal_edit') : t('buildings_setup.modal_add')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BuildingForm
            initialData={editingBuilding}
            onSubmit={handleSubmitBuilding}
            onCancel={() => setShowModal(false)}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BuildingsSetupPage;
